import React, { useEffect, useState } from 'react';
import './Footer.css';
import { NavLink } from 'react-router-dom';
import { getFooterSettings, getCategories } from '../../../services/settingsCache';

const socialIconMap = {
  facebook: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22 12.06C22 6.48 17.52 2 11.94 2 6.36 2 1.88 6.48 1.88 12.06c0 5 3.66 9.14 8.44 9.94v-7.03H7.99v-2.91h2.33V9.41c0-2.3 1.37-3.57 3.46-3.57.99 0 2.03.18 2.03.18v2.24h-1.14c-1.12 0-1.47.7-1.47 1.42v1.71h2.5l-.4 2.91h-2.1V22c4.78-.8 8.44-4.94 8.44-9.94Z" />
    </svg>
  ),
  x: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.146 2H21l-6.56 7.49L22.5 22h-6.91l-4.83-6.3L4.9 22H2.04l7.03-8.02L1.5 2h7.03l4.36 5.77L18.146 2Zm-2.42 18.02h2.29L8.35 3.9H6.01l9.716 16.12Z" />
    </svg>
  ),
  youtube: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M23.5 6.2a3.02 3.02 0 0 0-2.13-2.13C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.37.57A3.02 3.02 0 0 0 .5 6.2 31.6 31.6 0 0 0 0 12c0 1.95.2 3.9.5 5.8.28 1.06 1.08 1.85 2.13 2.13C4.5 20.5 12 20.5 12 20.5s7.5 0 9.37-.57a3.02 3.02 0 0 0 2.13-2.13c.3-1.9.5-3.85.5-5.8 0-1.95-.2-3.9-.5-5.8ZM9.75 15.5v-7L16 12l-6.25 3.5Z" />
    </svg>
  ),
  instagram: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M7.75 2h8.5A5.76 5.76 0 0 1 22 7.75v8.5A5.76 5.76 0 0 1 16.25 22h-8.5A5.76 5.76 0 0 1 2 16.25v-8.5A5.76 5.76 0 0 1 7.75 2Zm0 2A3.76 3.76 0 0 0 4 7.75v8.5A3.76 3.76 0 0 0 7.75 20h8.5A3.76 3.76 0 0 0 20 16.25v-8.5A3.76 3.76 0 0 0 16.25 4h-8.5ZM12 7.25A4.75 4.75 0 1 1 7.25 12 4.76 4.76 0 0 1 12 7.25Zm0 2A2.75 2.75 0 1 0 14.75 12 2.75 2.75 0 0 0 12 9.25Zm5-2.5a1.25 1.25 0 1 1-1.25 1.25A1.25 1.25 0 0 1 17 6.75Z" />
    </svg>
  ),
};

function formatPhone(value) {
  if (!value) return null;
  return value.startsWith('tel:') ? value : `tel:${value.replace(/[^\d+]/g, '')}`;
}

export default function Footer() {
  const [settings, setSettings] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadFooter = async () => {
      try {
        const [footerSettings, categoryData] = await Promise.all([
          getFooterSettings(),
          getCategories(),
        ]);

        if (cancelled) return;

        setSettings(footerSettings);
        setCategories(Array.isArray(categoryData) ? categoryData : []);
      } catch (err) {
        console.error('Failed to load footer data', err);
      }
    };

    loadFooter();

    return () => {
      cancelled = true;
    };
  }, []);

  const companyName = settings?.companyName || 'Tiwari Syndicate';
  const companyDescription =
    settings?.companyDescription ||
    'Premium construction materials, expert guidance, and dependable supply for residential and commercial projects.';
  const logoUrl = settings?.companyLogoUrl || `${process.env.PUBLIC_URL}/Group.svg`;
  const rawQuickLinks = settings?.footerQuickLinks || [
    { label: 'About', href: '/about' },
    { label: 'Services', href: '/services' },
    { label: 'Contact', href: '/contact' },
    { label: 'All Products', href: '/products' },
  ];
  const combinedLinks = [...rawQuickLinks];
  if (!combinedLinks.some(l => l.href === '/privacy-policy')) {
    combinedLinks.push({ label: 'Privacy Policy', href: '/privacy-policy' });
  }
  if (!combinedLinks.some(l => l.href === '/terms-of-use')) {
    combinedLinks.push({ label: 'Terms of Use', href: '/terms-of-use' });
  }
  const uniqueQuickLinks = combinedLinks.filter((link, idx, self) =>
    idx === self.findIndex(l => l.href?.toLowerCase() === link.href?.toLowerCase())
  );
  const socialLinks = settings?.footerSocialLinks || [];
  const mainCategories = categories.filter(cat => !cat.parentCategory);

  const gstin = settings?.companyGstin || '10AAJCT1476B1ZT';

  const contactRows = [
    settings?.companyAddress,
    settings?.companySecondaryAddress,
    settings?.companyPrimaryPhone,
    settings?.companySecondaryPhone,
    settings?.companySupportPhone,
    settings?.companyPrimaryEmail,
    settings?.companySecondaryEmail,
  ]
    .filter(Boolean)
    .filter((item, idx, self) =>
      idx === self.findIndex((r) => r.trim().toLowerCase() === item.trim().toLowerCase())
    );

  return (
    <footer className="site-footer" aria-labelledby="footer-title">
      <div className="site-footer__inner">
        <div className="footer__col footer__brand">
          <div className="footer__logo">
            <img src={logoUrl} alt={companyName} width="44" height="44" decoding="async" loading="lazy" />
            <span className="footer__brandname">{companyName}</span>
          </div>
          <p style={{ margin: '12px 0 0', color: '#9ca3af', lineHeight: 1.6 }}>
            {companyDescription}
          </p>
          <div className="footer__social">
            {socialLinks.map((item) => (
              <a
                key={`${item.platform}-${item.href}`}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                aria-label={item.label || item.platform}
              >
                {socialIconMap[item.platform?.toLowerCase()] || item.label || item.platform}
              </a>
            ))}
          </div>
          <div className="footer__copy">
            <div>{companyName} © {new Date().getFullYear()}. All Rights Reserved</div>
          </div>
        </div>
        <div className="footer__col">
          <h3 className="footer__title">Products</h3>
          <ul className="footer__links">
            {mainCategories.map((item) => (
              <li key={item._id || item.slug}>
                <NavLink to={`/products/${item.slug}`}>{item.name}</NavLink>
              </li>
            ))}
            {mainCategories.length === 0 && (
              <li style={{ color: '#9ca3af', fontSize: 13 }}>Categories loading...</li>
            )}
          </ul>
        </div>
        <div className="footer__col">
          <h3 className="footer__title">Quick Links</h3>
          <ul className="footer__links">
            {uniqueQuickLinks.map((item) => (
              <li key={`${item.label}-${item.href}`}>
                <NavLink to={item.href}>{item.label}</NavLink>
              </li>
            ))}
          </ul>
        </div>
        <div className="footer__col">
          <h3 className="footer__title">Contact Information</h3>
          <ul className="footer__links">
            {contactRows.map((item) => {
              const isEmail = item.includes('@');
              const isPhone = !isEmail && /\d/.test(item) && !item.toLowerCase().includes('chowk') && !item.toLowerCase().includes('chawk') && !item.toLowerCase().includes('bihar') && !item.toLowerCase().includes('muzaffarpur');
              const isAddress = !isEmail && !isPhone;
              const href = isEmail
                ? `mailto:${item}`
                : isPhone
                ? formatPhone(item)
                : isAddress
                ? 'https://www.google.com/maps/search/?api=1&query=Tiwari+Syndicate+Private+Limited,+Near+Majhaulia+Chowk,+Majhaulia,+Muzaffarpur,+Bihar+842001'
                : null;

              return (
                <li key={item}>
                  {href ? (
                    <a
                      href={href}
                      target={isAddress ? '_blank' : undefined}
                      rel={isAddress ? 'noopener noreferrer' : undefined}
                      style={{ color: 'inherit', textDecoration: isAddress ? 'underline' : 'none' }}
                      title={isAddress ? 'Click to view real location in Google Maps' : undefined}
                    >
                      {item}
                      {isAddress && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '6px', fontSize: '11px', color: '#60a5fa', fontWeight: '600' }}>
                          📍 View on Map
                        </span>
                      )}
                    </a>
                  ) : (
                    item
                  )}
                </li>
              );
            })}
            <li style={{ color: '#d1d5db', marginTop: '2px', fontSize: '13px' }}>
              <span style={{ color: '#9ca3af' }}>GSTIN: </span>
              <span style={{ color: '#e5e7eb', fontFamily: 'monospace', letterSpacing: '0.5px' }}>{gstin}</span>
            </li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
